// ============================================================================
// GESTIMMONY — Import CSV depuis un autre outil (Rentila, Smovin, Applicéo…)
// Upload CSV → mapping auto des colonnes → aperçu → import en 1 clic.
// ============================================================================
import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Upload, FileSpreadsheet, Building2, Users, Check, Loader2, ArrowRight, X,
} from "lucide-react";

const FIELDS = {
  properties: [
    { key: "label", label: "Nom du bien", required: true },
    { key: "address", label: "Adresse" },
    { key: "city", label: "Ville" },
    { key: "postal_code", label: "Code postal" },
    { key: "type", label: "Type (appartement, studio…)" },
    { key: "surface", label: "Surface (m²)" },
    { key: "rooms", label: "Nombre de pièces" },
    { key: "purchase_price", label: "Prix d'achat" },
    { key: "monthly_charges", label: "Charges mensuelles" },
    { key: "taxe_fonciere", label: "Taxe foncière" },
    { key: "notes", label: "Notes" },
  ],
  tenants: [
    { key: "first_name", label: "Prénom" },
    { key: "last_name", label: "Nom", required: true },
    { key: "email", label: "Email" },
    { key: "phone", label: "Téléphone" },
    { key: "birth_date", label: "Date de naissance" },
    { key: "profession", label: "Profession" },
    { key: "monthly_income", label: "Revenu mensuel" },
    { key: "guarantor_name", label: "Garant" },
    { key: "guarantor_phone", label: "Téléphone garant" },
    { key: "notes", label: "Notes" },
  ],
};

const ENTITIES = [
  { key: "properties", label: "Biens", icon: Building2, hint: "Importer vos lots / logements" },
  { key: "tenants", label: "Locataires", icon: Users, hint: "Importer vos locataires" },
];

const NONE = "__none";

export default function ImportDataPage() {
  const [entity, setEntity] = useState("properties");
  const [parsed, setParsed] = useState(null);     // {columns, rows, suggested}
  const [mapping, setMapping] = useState({});
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const reset = () => { setParsed(null); setMapping({}); setResult(null); if (fileRef.current) fileRef.current.value = ""; };

  const onFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) { toast.error("Veuillez sélectionner un fichier .csv"); return; }
    setParsing(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/parse", fd);
      setParsed(data);
      setMapping(data.suggested?.[entity] || {});
    } catch (e) {
      toast.error("Lecture impossible : " + (e.response?.data?.detail || e.message));
    } finally { setParsing(false); }
  };

  const switchEntity = (k) => {
    setEntity(k);
    setResult(null);
    if (parsed) setMapping(parsed.suggested?.[k] || {});
  };

  const setField = (fieldKey, col) => {
    setMapping((m) => {
      const next = { ...m };
      if (col === NONE) delete next[fieldKey];
      else next[fieldKey] = col;
      return next;
    });
  };

  const mappedCount = Object.keys(mapping).length;

  const doImport = async () => {
    const reqField = FIELDS[entity].find((f) => f.required);
    if (reqField && !mapping[reqField.key]) {
      toast.error(`Le champ « ${reqField.label} » doit être associé à une colonne`);
      return;
    }
    setImporting(true); setResult(null);
    try {
      const { data } = await api.post("/import/commit", {
        entity, mapping, rows: parsed.rows,
      });
      setResult(data);
      toast.success(`${data.created} ${entity === "properties" ? "bien(s)" : "locataire(s)"} importé(s)`);
    } catch (e) {
      toast.error("Import impossible : " + (e.response?.data?.detail || e.message));
    } finally { setImporting(false); }
  };

  const previewRows = parsed ? parsed.rows.slice(0, 5) : [];

  return (
    <div className="space-y-6" data-testid="import-page">
      <div>
        <h2 className="font-heading text-xl font-bold text-slate-900 tracking-tight">Importer mes données</h2>
        <p className="mt-1 text-slate-600 text-sm">
          Migrez vos biens et locataires depuis Rentila, Smovin, Applicéo ou un simple tableur.
          Exportez un fichier <b>.csv</b> depuis votre ancien outil, déposez-le ici, vérifiez les
          colonnes et importez en un clic.
        </p>
      </div>

      {/* Choix entité */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {ENTITIES.map((e) => (
          <button
            key={e.key}
            data-testid={`import-entity-${e.key}`}
            onClick={() => switchEntity(e.key)}
            className={`flex items-center gap-3 p-4 rounded-xl border text-left transition ${
              entity === e.key
                ? "border-teal-500 bg-teal-50 ring-2 ring-teal-500/20"
                : "border-slate-200 bg-white hover:border-slate-300"
            }`}
          >
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${entity === e.key ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-600"}`}>
              <e.icon className="h-5 w-5" />
            </div>
            <div>
              <div className="font-heading font-semibold text-slate-900">{e.label}</div>
              <div className="text-xs text-slate-500">{e.hint}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Upload */}
      {!parsed && (
        <Card className="p-8">
          <div
            className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center hover:border-teal-400 transition cursor-pointer"
            onClick={() => fileRef.current?.click()}
            data-testid="import-dropzone"
          >
            {parsing ? (
              <div className="flex flex-col items-center gap-3 text-slate-500">
                <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
                Analyse du fichier…
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="h-14 w-14 rounded-2xl bg-teal-50 text-teal-700 flex items-center justify-center">
                  <Upload className="h-7 w-7" />
                </div>
                <div className="font-heading font-semibold text-slate-900">Déposez votre fichier CSV</div>
                <div className="text-xs text-slate-500">ou cliquez pour parcourir — séparateur virgule ou point-virgule détecté automatiquement</div>
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              hidden
              data-testid="import-file-input"
              onChange={(e) => onFile(e.target.files?.[0])}
            />
          </div>
        </Card>
      )}

      {/* Mapping + aperçu */}
      {parsed && (
        <>
          <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <FileSpreadsheet className="h-4 w-4 text-teal-700" />
                {parsed.count} ligne(s) détectée(s) · {parsed.columns.length} colonne(s)
              </div>
              <Button variant="ghost" size="sm" onClick={reset} data-testid="import-reset">
                <X className="h-4 w-4 mr-1" /> Changer de fichier
              </Button>
            </div>

            <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">
              Correspondance des colonnes
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {FIELDS[entity].map((f) => (
                <div key={f.key} className="flex items-center gap-2">
                  <div className="w-40 shrink-0 text-sm text-slate-700">
                    {f.label} {f.required && <span className="text-rose-500">*</span>}
                  </div>
                  <Select value={mapping[f.key] || NONE} onValueChange={(v) => setField(f.key, v)}>
                    <SelectTrigger className="flex-1" data-testid={`import-map-${f.key}`}>
                      <SelectValue placeholder="— Ignorer —" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>— Ignorer —</SelectItem>
                      {parsed.columns.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </Card>

          {/* Aperçu */}
          <Card className="p-5 overflow-x-auto">
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-3">
              Aperçu (5 premières lignes)
            </div>
            <table className="w-full text-sm" data-testid="import-preview-table">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                  {FIELDS[entity].filter((f) => mapping[f.key]).map((f) => (
                    <th key={f.key} className="py-2 pr-4 font-semibold">{f.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    {FIELDS[entity].filter((f) => mapping[f.key]).map((f) => (
                      <td key={f.key} className="py-2 pr-4 text-slate-700 whitespace-nowrap">
                        {row[mapping[f.key]] || <span className="text-slate-300">—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {mappedCount === 0 && (
              <div className="text-sm text-slate-400 py-4">Associez au moins une colonne pour voir l'aperçu.</div>
            )}
          </Card>

          <div className="flex items-center justify-end gap-3">
            <Button
              onClick={doImport}
              disabled={importing || mappedCount === 0 || !!result}
              data-testid="import-commit-btn"
              className="bg-[hsl(var(--primary))]"
            >
              {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />}
              {importing ? "Import en cours…" : `Importer ${parsed.count} ligne(s)`}
            </Button>
          </div>
        </>
      )}

      {/* Résultat */}
      {result && (
        <Card className="p-5 border-emerald-200 bg-emerald-50" data-testid="import-result">
          <div className="flex items-center gap-2 text-emerald-800 font-semibold">
            <Check className="h-5 w-5" /> {result.created} enregistrement(s) importé(s) avec succès
          </div>
          {result.error_count > 0 && (
            <div className="mt-2 text-sm text-amber-800">
              {result.error_count} ligne(s) ignorée(s) (données incomplètes ou invalides).
            </div>
          )}
          {result.skipped > 0 && (
            <div className="mt-1 text-sm text-slate-600">
              {result.skipped} doublon(s) ignoré(s) (déjà présent dans votre compte).
            </div>
          )}
          <div className="mt-3 text-sm text-emerald-900">
            Retrouvez vos données dans la section {entity === "properties" ? "Biens" : "Locataires"}.
          </div>
        </Card>
      )}
    </div>
  );
}
